import { useEffect, useState, useCallback } from 'react'
import {
  Building2, Plus, RefreshCw, X,
  ToggleLeft, ToggleRight, ChevronDown,
  Shield, Phone, MessageCircle, Palette, Sliders, Pencil, CreditCard, Trash2,
  CheckCircle, AlertTriangle,
} from 'lucide-react'
import {
  adminService,
  type Tenant, type AppUser, type Wallet as WalletType,
  type TelephonyConfig, type WhatsAppConfig,
  type TenantThemeConfig,
} from '../services/admin'
import { billingService } from '../../billing/services/billing'
import { UserRole, type PricingCatalog, type BillingService } from '../../../types'
import PasswordInput from '../../../components/PasswordInput'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number, currency = 'INR') =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 2 }).format(n)

const fmtDate = (s: string) =>
  new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })


// ── Stat Card ────────────────────────────────────────────────────────────────

// ── New Dealer Modal ─────────────────────────────────────────────────────

function validateMobile(v: string): string | null {
  const digits = v.replace(/\D/g, '')
  if (!digits) return null
  if (digits.length < 10) return 'Minimum 10 digits required'
  if (digits.length > 15) return 'Maximum 15 digits allowed'
  return null
}

function validateEmail(v: string): string | null {
  if (!v) return null
  if (/\s/.test(v)) return 'Spaces are not allowed in email'
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return 'Enter a valid email address (e.g. name@domain.com)'
  return null
}

function validateDomain(v: string): string | null {
  if (!v) return null
  if (/[^a-z0-9.-]/.test(v)) return 'Only lowercase letters, numbers, hyphens, and dots allowed'
  return null
}

function filterMobile(v: string): string {
  // Allow digits and optional leading +
  if (v === '') return ''
  const cleaned = v.replace(/[^+\d]/g, '')
  // Only allow + at the start
  const plusCount = (cleaned.match(/\+/g) || []).length
  if (plusCount > 1) return cleaned.replace(/\+/g, '').replace(/^/, '')
  if (plusCount === 1 && !cleaned.startsWith('+')) return cleaned.replace(/\+/g, '')
  return cleaned
}

function filterDomain(v: string): string {
  return v.toLowerCase().replace(/[^a-z0-9.-]/g, '')
}

function filterAmount(v: string): string {
  // Allow digits and at most one decimal point
  const parts = v.replace(/[^\d.]/g, '').split('.')
  if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('')
  return v.replace(/[^\d.]/g, '')
}

function OnboardDealerModal({ onClose, onDone }: {
  onClose: () => void; onDone: () => void
}) {
  const [form, setForm] = useState({
    tenant_name: '', domain: '',
    dp_full_name: '', dp_email: '', dp_mobile: '', dp_password: '',
    initial_wallet_credit: '', credit_description: 'Initial wallet credit',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }))

  const setErrorField = (field: string, msg: string | null) =>
    setErrors(prev => {
      if (msg === null) {
        const { [field]: _, ...rest } = prev; return rest
      }
      return { ...prev, [field]: msg }
    })

  const handleChange = (field: string, raw: string) => {
    let val = raw
    if (field === 'dp_mobile') val = filterMobile(raw)
    if (field === 'domain') val = filterDomain(raw)
    if (field === 'initial_wallet_credit') val = filterAmount(raw)
    set(field, val)
    // Clear error on valid input
    if (field === 'dp_mobile') setErrorField('dp_mobile', validateMobile(val))
    if (field === 'domain') setErrorField('domain', validateDomain(val))
    if (field === 'dp_email') setErrorField('dp_email', validateEmail(val))
  }

  const handleBlur = (field: string) => {
    const val = form[field as keyof typeof form]
    let msg: string | null = null
    if (field === 'dp_mobile') msg = validateMobile(val)
    if (field === 'dp_email') msg = validateEmail(val)
    if (field === 'domain') msg = validateDomain(val)
    setErrorField(field, msg)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // Validate all fields
    const newErrors: Record<string, string> = {}
    const mobileErr = validateMobile(form.dp_mobile)
    const emailErr = validateEmail(form.dp_email)
    const domainErr = validateDomain(form.domain)
    if (!form.tenant_name.trim()) newErrors.tenant_name = 'Company name is required'
    if (!form.domain.trim()) newErrors.domain = 'Domain is required'
    else if (domainErr) newErrors.domain = domainErr
    if (!form.dp_full_name.trim()) newErrors.dp_full_name = 'Full name is required'
    if (!form.dp_email.trim()) newErrors.dp_email = emailErr || 'Email is required'
    else if (emailErr) newErrors.dp_email = emailErr
    if (!form.dp_mobile.trim()) newErrors.dp_mobile = 'Mobile is required'
    else if (mobileErr) newErrors.dp_mobile = mobileErr
    if (form.dp_password.length < 12) newErrors.dp_password = 'Password must be at least 12 characters'
    if (form.initial_wallet_credit && !/^\d*\.?\d*$/.test(form.initial_wallet_credit)) newErrors.initial_wallet_credit = 'Only numbers allowed'

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    setSaving(true)
    setError('')
    try {
      await adminService.onboardDealer({
        tenant_name: form.tenant_name,
        domain: form.domain,
        dp_full_name: form.dp_full_name,
        dp_email: form.dp_email,
        dp_mobile: form.dp_mobile,
        dp_password: form.dp_password,
        initial_wallet_credit: form.initial_wallet_credit ? Number(form.initial_wallet_credit) : undefined,
        credit_description: form.credit_description || undefined,
      })
      onDone()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Onboarding failed. Check the details and try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              New Dealer
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              Creates the tenant, Dealer Principal account, and wallet atomically.
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
          {/* Tenant section */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Tenant
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group col-span-2">
                <label className="form-label">Company Name <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input className="input" required placeholder="Apex Motors Pvt. Ltd."
                  value={form.tenant_name} onChange={e => handleChange('tenant_name', e.target.value)} onBlur={() => handleBlur('tenant_name')} />
                {errors.tenant_name && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.tenant_name}</p>}
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">Domain <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input className="input" required placeholder="apexmotors"
                  value={form.domain} onChange={e => handleChange('domain', e.target.value)} onBlur={() => handleBlur('domain')} />
                {errors.domain && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.domain}</p>}
                {!errors.domain && <p className="form-hint">Unique slug — lowercase letters, numbers, hyphens, and dots (e.g. khanna-motors.local)</p>}
              </div>
            </div>
          </div>

          <div className="divider" />

          {/* Dealer Principal section */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Dealer Principal
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group col-span-2">
                <label className="form-label">Full Name <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input className="input" required placeholder="Rajesh Kumar"
                  value={form.dp_full_name} onChange={e => handleChange('dp_full_name', e.target.value)} onBlur={() => handleBlur('dp_full_name')} />
                {errors.dp_full_name && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.dp_full_name}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Email <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input className="input" required type="email" placeholder="rajesh@apexmotors.com"
                  value={form.dp_email} onChange={e => handleChange('dp_email', e.target.value)} onBlur={() => handleBlur('dp_email')} />
                {errors.dp_email && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.dp_email}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Mobile <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input className="input" required type="tel" placeholder="+919876543210"
                  value={form.dp_mobile} onChange={e => handleChange('dp_mobile', e.target.value)} onBlur={() => handleBlur('dp_mobile')} />
                {errors.dp_mobile && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.dp_mobile}</p>}
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">Temporary Password <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <PasswordInput required minLength={12} placeholder="Min. 12 characters"
                  autoComplete="new-password"
                  value={form.dp_password} onChange={e => handleChange('dp_password', e.target.value)} onBlur={() => handleBlur('dp_password')} />
                {errors.dp_password && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.dp_password}</p>}
                {!errors.dp_password && <p className="form-hint">Min. 12 characters — dealer must change on first login</p>}
              </div>
            </div>
          </div>

          <div className="divider" />

          {/* Wallet credit section */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
              Initial Wallet Credit <span className="normal-case font-normal" style={{ color: 'var(--text-tertiary)' }}>(optional)</span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group">
                <label className="form-label">Amount (INR)</label>
                <input className="input" placeholder="0.00" inputMode="decimal"
                  value={form.initial_wallet_credit} onChange={e => handleChange('initial_wallet_credit', e.target.value)} onBlur={() => handleBlur('initial_wallet_credit')} />
                {errors.initial_wallet_credit && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.initial_wallet_credit}</p>}
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <input className="input" placeholder="Initial wallet credit"
                  value={form.credit_description} onChange={e => handleChange('credit_description', e.target.value)} />
              </div>
            </div>
          </div>

          {error && (
            <div className="alert-danger text-sm">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'New Dealer'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Channel Config Modal ─────────────────────────────────────────────────────

function ChannelConfigModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }) {
  const [activePanel, setActivePanel] = useState<'telephony' | 'whatsapp'>('telephony')
  const [telephony, setTelephony] = useState<TelephonyConfig | null>(null)
  const [whatsapp, setWhatsapp] = useState<WhatsAppConfig | null>(null)
  const [loadingTel, setLoadingTel] = useState(true)
  const [loadingWA, setLoadingWA] = useState(true)

  const [telForm, setTelForm] = useState({
    provider: 'exotel' as 'exotel' | 'twilio' | 'myoperator',
    account_sid: '', subdomain: 'api.in.exotel.com', api_key: '', api_secret: '',
    virtual_number: '', webhook_token: '', call_time_limit: '3600', ring_timeout: '30',
  })
  const [waForm, setWaForm] = useState({
    phone_number_id: '', api_token: '', business_name: '', contact_phone: '',
  })

  const [savingTel, setSavingTel] = useState(false)
  const [savingWA, setSavingWA] = useState(false)
  const [telError, setTelError] = useState('')
  const [waError, setWaError] = useState('')

  useEffect(() => {
    adminService.getTelephonyConfig(tenant.id)
      .then(cfg => {
        setTelephony(cfg)
        setTelForm(f => ({
          ...f,
          provider: (cfg.provider as 'exotel' | 'twilio' | 'myoperator') || 'exotel',
          account_sid: cfg.account_sid,
          subdomain: cfg.subdomain || '',
          virtual_number: cfg.virtual_number,
          call_time_limit: String(cfg.call_time_limit),
          ring_timeout: String(cfg.ring_timeout),
        }))
      })
      .catch(() => {})
      .finally(() => setLoadingTel(false))

    adminService.getWhatsAppConfig(tenant.id)
      .then(cfg => {
        setWhatsapp(cfg)
        setWaForm(f => ({
          ...f,
          phone_number_id: cfg.phone_number_id,
          business_name: cfg.business_name ?? '',
          contact_phone: cfg.contact_phone ?? '',
        }))
      })
      .catch(() => {})
      .finally(() => setLoadingWA(false))
  }, [tenant.id])

  const saveTelephony = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingTel(true)
    setTelError('')
    try {
      const cfg = await adminService.upsertTelephonyConfig(tenant.id, {
        provider: telForm.provider,
        account_sid: telForm.account_sid,
        subdomain: telForm.subdomain,
        api_key: telForm.api_key,
        api_secret: telForm.api_secret,
        virtual_number: telForm.virtual_number,
        webhook_token: telForm.webhook_token,
        call_time_limit: Number(telForm.call_time_limit),
        ring_timeout: Number(telForm.ring_timeout),
      })
      setTelephony(cfg)
      setTelForm(f => ({ ...f, api_key: '', api_secret: '', webhook_token: '' }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setTelError(msg || 'Failed to save telephony config')
    } finally {
      setSavingTel(false)
    }
  }

  const saveWhatsApp = async (e: React.FormEvent) => {
    e.preventDefault()
    setSavingWA(true)
    setWaError('')
    try {
      const cfg = await adminService.upsertWhatsAppConfig(tenant.id, {
        phone_number_id: waForm.phone_number_id,
        api_token: waForm.api_token,
        business_name: waForm.business_name || undefined,
        contact_phone: waForm.contact_phone || undefined,
      })
      setWhatsapp(cfg)
      setWaForm(f => ({ ...f, api_token: '' }))
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setWaError(msg || 'Failed to save WhatsApp config')
    } finally {
      setSavingWA(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Channel Config
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{tenant.name}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {/* Sub-tabs */}
        <div className="flex gap-1 mb-5" style={{ borderBottom: '1px solid var(--border-default)' }}>
          {(['telephony', 'whatsapp'] as const).map(p => (
            <button
              key={p}
              onClick={() => setActivePanel(p)}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors"
              style={{
                borderBottomColor: activePanel === p ? 'var(--brand-primary)' : 'transparent',
                color: activePanel === p ? 'var(--brand-primary)' : 'var(--text-tertiary)',
              }}
            >
              {p === 'telephony' ? <Phone className="h-3.5 w-3.5" /> : <MessageCircle className="h-3.5 w-3.5" />}
              {p === 'telephony' ? 'Telephony' : 'WhatsApp'}
              {p === 'telephony' && !loadingTel && telephony && (
                <span className="badge-success ml-1" style={{ fontSize: 10, padding: '1px 5px' }}>Set</span>
              )}
              {p === 'whatsapp' && !loadingWA && whatsapp && (
                <span className="badge-success ml-1" style={{ fontSize: 10, padding: '1px 5px' }}>Set</span>
              )}
            </button>
          ))}
        </div>

        {/* Telephony panel */}
        {activePanel === 'telephony' && (
          <form onSubmit={saveTelephony} className="space-y-3" autoComplete="off">
            {telephony && (
              <div className="alert-info text-sm">
                Currently using <strong>{telephony.provider}</strong> · key …{telephony.api_key_hint} · {telephony.virtual_number}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group">
                <label className="form-label">Account SID</label>
                <input className="input font-mono text-xs" required value={telForm.account_sid}
                  onChange={e => setTelForm(f => ({ ...f, account_sid: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Sub Domain</label>
                <input className="input font-mono text-xs" required value={telForm.subdomain}
                  onChange={e => setTelForm(f => ({ ...f, subdomain: e.target.value }))}
                  placeholder="api.in.exotel.com" />
              </div>
              <div className="form-group">
                <label className="form-label">API Key <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(AuthKey)</span></label>
                <input className="input font-mono text-xs" required={!telephony} placeholder={telephony ? '(unchanged)' : ''}
                  autoComplete="off"
                  value={telForm.api_key} onChange={e => setTelForm(f => ({ ...f, api_key: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">API Secret <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(AuthToken)</span></label>
                <PasswordInput className="input font-mono text-xs" required={!telephony} placeholder={telephony ? '(unchanged)' : ''}
                  autoComplete="new-password"
                  value={telForm.api_secret} onChange={e => setTelForm(f => ({ ...f, api_secret: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Virtual Number</label>
                <input className="input" required value={telForm.virtual_number}
                  onChange={e => setTelForm(f => ({ ...f, virtual_number: e.target.value }))} placeholder="+912233445566" />
              </div>
              <div className="form-group">
                <label className="form-label">Webhook Token</label>
                <input className="input font-mono text-xs" required={!telephony} placeholder={telephony ? '(unchanged)' : ''}
                  autoComplete="new-password"
                  value={telForm.webhook_token} onChange={e => setTelForm(f => ({ ...f, webhook_token: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Call Time Limit (s)</label>
                <input className="input" type="number" min="60" max="7200" value={telForm.call_time_limit}
                  onChange={e => setTelForm(f => ({ ...f, call_time_limit: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Ring Timeout (s)</label>
                <input className="input" type="number" min="10" max="120" value={telForm.ring_timeout}
                  onChange={e => setTelForm(f => ({ ...f, ring_timeout: e.target.value }))} />
              </div>
            </div>
            {telError && <div className="alert-danger text-sm">{telError}</div>}
            <div className="flex justify-end pt-1">
              <button type="submit" className="btn-primary" disabled={savingTel}>
                {savingTel ? 'Saving…' : telephony ? 'Update Config' : 'Save Config'}
              </button>
            </div>
          </form>
        )}

        {/* WhatsApp panel */}
        {activePanel === 'whatsapp' && (
          <form onSubmit={saveWhatsApp} className="space-y-3" autoComplete="off">
            {whatsapp && (
              <div className="alert-info text-sm">
                Phone ID <strong>{whatsapp.phone_number_id}</strong> · token …{whatsapp.api_token_hint}
                {whatsapp.business_name && ` · ${whatsapp.business_name}`}
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group col-span-2">
                <label className="form-label">Phone Number ID</label>
                <input className="input font-mono text-xs" required value={waForm.phone_number_id}
                  onChange={e => setWaForm(f => ({ ...f, phone_number_id: e.target.value }))} />
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">API Token</label>
                <PasswordInput className="input font-mono text-xs" required={!whatsapp} placeholder={whatsapp ? '(unchanged)' : ''}
                  autoComplete="new-password"
                  value={waForm.api_token} onChange={e => setWaForm(f => ({ ...f, api_token: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Business Name <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span></label>
                <input className="input" value={waForm.business_name}
                  onChange={e => setWaForm(f => ({ ...f, business_name: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Contact Phone <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(optional)</span></label>
                <input className="input" value={waForm.contact_phone}
                  onChange={e => setWaForm(f => ({ ...f, contact_phone: e.target.value }))} placeholder="+91…" />
              </div>
            </div>
            {waError && <div className="alert-danger text-sm">{waError}</div>}
            <div className="flex justify-end pt-1">
              <button type="submit" className="btn-primary" disabled={savingWA}>
                {savingWA ? 'Saving…' : whatsapp ? 'Update Config' : 'Save Config'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalOverlay>
  )
}

// ── Feature Flags Modal ──────────────────────────────────────────────────────

function FeatureFlagsModal({ tenant, onClose, onSaved }: {
  tenant: Tenant
  onClose: () => void
  onSaved: (updated: Tenant) => void
}) {
  // Represent flags as an editable JSON string
  const [raw, setRaw] = useState(
    JSON.stringify(tenant.feature_flags ?? {}, null, 2)
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    let flags: Record<string, unknown>
    try {
      flags = JSON.parse(raw)
      if (typeof flags !== 'object' || Array.isArray(flags)) throw new Error()
    } catch {
      setError('Invalid JSON — must be a flat key/value object, e.g. {"whatsapp_enabled": true}')
      return
    }

    setSaving(true)
    try {
      const updated = await adminService.updateFeatureFlags(tenant.id, flags)
      // Parent shows a page-level toast and closes the modal.
      onSaved(updated)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to save feature flags')
      setSaving(false)
    }
  }

  // Quick-toggle helper row for common boolean flags
  const parsed: Record<string, unknown> = (() => {
    try { return JSON.parse(raw) } catch { return {} }
  })()

  const toggleFlag = (key: string) => {
    const current = { ...parsed, [key]: !parsed[key] }
    setRaw(JSON.stringify(current, null, 2))
  }

  const COMMON_FLAGS = [
    { key: 'whatsapp_enabled',          label: 'WhatsApp Messaging' },
    { key: 'ai_extraction_enabled',     label: 'AI Quote Extraction' },
    { key: 'ai_analysis_on_demand',     label: 'AI Analysis On Demand' },
    { key: 'smart_connect_enabled',     label: 'Smart Connect' },
    { key: 'fraud_detection_enabled',   label: 'Fraud Detection' },
    { key: 'renewal_forecast_enabled',  label: 'Renewal Forecast' },
    { key: 'data_export_enabled',       label: 'Data Export' },
    { key: 'reporting_enabled',         label: 'Reporting & Dashboard' },
    { key: 'audit_logs_enabled',        label: 'Audit Logs' },
    { key: 'discount_workflow_enabled', label: 'Discount Approval Workflow' },
    { key: 'custom_mapping_enabled',    label: 'Custom Column Mapping' },
  ]

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Feature Flags
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name} · flags are merged (not replaced) on save
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Quick toggles — single bounded list so it's clear nothing is truncated */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>
                Feature Toggles
              </p>
              <span className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                {COMMON_FLAGS.filter(f => Boolean(parsed[f.key])).length} of {COMMON_FLAGS.length} on
              </span>
            </div>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid var(--border-default)' }}
            >
              {COMMON_FLAGS.map(({ key, label }, i) => {
                const isOn = Boolean(parsed[key])
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between px-3 py-2.5 cursor-pointer transition-colors"
                    style={{
                      borderTop: i === 0 ? 'none' : '1px solid var(--border-default)',
                      background: isOn ? 'var(--surface-subtle)' : 'transparent',
                    }}
                    onClick={() => toggleFlag(key)}
                  >
                    <p className="text-sm" style={{ color: 'var(--text-primary)' }}>{label}</p>
                    {isOn
                      ? <ToggleRight className="h-5 w-5 shrink-0" style={{ color: 'var(--success-text)' }} />
                      : <ToggleLeft className="h-5 w-5 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
                    }
                  </div>
                )
              })}
            </div>
            <p className="form-hint mt-1.5">All available features shown above.</p>
          </div>

          {error && <div className="alert-danger text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Close</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Flags'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Branding Modal ───────────────────────────────────────────────────────────

function BrandingModal({ tenant, onClose, onSaved }: { tenant: Tenant; onClose: () => void; onSaved?: () => void }) {
  // Pre-populate from previously saved theme stored in tenant.settings.theme
  const savedTheme = (tenant.settings?.theme ?? {}) as TenantThemeConfig
  const [form, setForm] = useState<TenantThemeConfig>({
    app_name:      savedTheme.app_name      ?? '',
    tagline:       savedTheme.tagline       ?? '',
    primary_color: savedTheme.primary_color ?? '#7b74e2',
    logo_url:      savedTheme.logo_url      ?? '',
  })
  const [logoPreview, setLogoPreview]   = useState<string | null>(savedTheme.logo_url ?? null)
  const [saving, setSaving]             = useState(false)
  const [saved,  setSaved]              = useState(false)
  const [error,  setError]              = useState('')

  const set = (k: keyof TenantThemeConfig, v: string) =>
    setForm(f => ({ ...f, [k]: v }))

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (file.size > 200 * 1024) {
      setError('Logo must be under 200 KB')
      return
    }
    setError('')
    const reader = new FileReader()
    reader.onload = () => {
      const dataUri = reader.result as string
      set('logo_url', dataUri)
      setLogoPreview(dataUri)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveLogo = () => {
    set('logo_url', '')
    setLogoPreview(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setSaved(false); setError('')
    try {
      const payload: TenantThemeConfig = {}
      if (form.app_name?.trim())    payload.app_name      = form.app_name.trim()
      if (form.tagline?.trim())     payload.tagline       = form.tagline.trim()
      if (form.primary_color)       payload.primary_color = form.primary_color
      payload.logo_url = form.logo_url?.trim() || null
      await adminService.updateTenantTheme(tenant.id, payload)
      setSaved(true)
      onSaved?.()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to save branding')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              Branding
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name} · Customise login page and sidebar appearance
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="form-group col-span-2">
              <label className="form-label">App Name</label>
              <input
                className="input"
                placeholder="e.g. ABC Motors Insurance"
                value={form.app_name}
                onChange={e => set('app_name', e.target.value)}
              />
              <p className="form-hint">Shown in sidebar and login page. Leave blank for default.</p>
            </div>
            <div className="form-group col-span-2">
              <label className="form-label">Tagline</label>
              <input
                className="input"
                placeholder="e.g. Powered by ABC Motors"
                value={form.tagline}
                onChange={e => set('tagline', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Primary Colour</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="h-9 w-14 rounded cursor-pointer border"
                  style={{ borderColor: 'var(--border-default)', padding: '2px 3px' }}
                  value={form.primary_color || '#7b74e2'}
                  onChange={e => set('primary_color', e.target.value)}
                />
                <input
                  className="input font-mono text-xs flex-1"
                  placeholder="#7b74e2"
                  value={form.primary_color}
                  onChange={e => set('primary_color', e.target.value)}
                />
              </div>
              <p className="form-hint">Buttons, active states, sidebar accent</p>
            </div>

            {/* Logo upload */}
            <div className="form-group">
              <label className="form-label">
                Logo
                <span className="ml-1 font-normal" style={{ color: 'var(--text-tertiary)' }}>(optional)</span>
              </label>
              <div className="flex items-center gap-3">
                {/* Preview */}
                <div
                  className="h-10 w-20 rounded flex items-center justify-center shrink-0 overflow-hidden"
                  style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="max-h-9 max-w-full object-contain" />
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>None</span>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <label
                    className="btn-secondary text-xs cursor-pointer"
                    style={{ display: 'inline-block', padding: '4px 10px' }}
                  >
                    {logoPreview ? 'Replace' : 'Upload'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                  </label>
                  {logoPreview && (
                    <button
                      type="button"
                      className="text-xs"
                      style={{ color: 'var(--danger-text)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
                      onClick={handleRemoveLogo}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <p className="form-hint">JPEG, PNG, WebP or GIF · max 200 KB</p>
            </div>
          </div>

          {/* Live preview chip */}
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-lg"
            style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
          >
            {logoPreview ? (
              <img src={logoPreview} alt="" className="h-7 w-auto object-contain shrink-0" />
            ) : (
              <div className="w-7 h-7 rounded-full shrink-0" style={{ background: form.primary_color || 'var(--brand-primary)' }} />
            )}
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                {form.app_name?.trim() || tenant.name}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                {form.tagline?.trim() || 'Preview tagline'}
              </p>
            </div>
          </div>

          {error && <div className="alert-danger text-sm">{error}</div>}
          {saved && <div className="alert-success text-sm">Branding saved — takes effect on next page load.</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Close</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Branding'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Stats Strip ──────────────────────────────────────────────────────────────
// One sentence of context — replaces the 4 oversized stat cards entirely.

function StatsStrip({ tenants, users, wallets, loading }: {
  tenants: Tenant[]; users: AppUser[]; wallets: WalletType[]; loading: boolean
}) {
  const activeTenants  = tenants.filter(t => t.is_active).length
  const activeUsers    = users.filter(u => u.is_active).length
  const totalBalance   = wallets.reduce((sum, w) => sum + Number(w.balance), 0)
  const avgBalance     = wallets.length ? totalBalance / wallets.length : 0

  if (loading) return <p className="page-subtitle">Loading…</p>

  const sep = <span style={{ color: 'var(--border-strong)', margin: '0 6px' }}>·</span>

  return (
    <p className="page-subtitle" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0, marginTop: 2 }}>
      <span>
        <strong style={{ color: 'var(--brand-primary)' }}>{activeTenants}</strong>
        <span> of {tenants.length} tenants</span>
      </span>
      {sep}
      <span>
        <strong style={{ color: 'var(--success-text)' }}>{activeUsers}</strong>
        <span> active users</span>
      </span>
      {sep}
      <span>
        <strong style={{ color: 'var(--warning-text)' }}>{fmt(totalBalance)}</strong>
        <span> platform funds</span>
      </span>
      {sep}
      <span>
        <strong style={{ color: 'var(--info-text)' }}>{fmt(avgBalance)}</strong>
        <span> avg wallet</span>
      </span>
    </p>
  )
}

// ── Edit Dealer Modal ─────────────────────────────────────────────────────────
// Combines company details (name, domain) + Dealer Principal fields in one modal.

function EditDealerModal({ tenant, dp, onClose, onSaved }: {
  tenant: Tenant
  dp: AppUser
  onClose: () => void
  onSaved: (updatedTenant: Tenant, updatedDP: AppUser) => void
}) {
  const [company, setCompany] = useState({ name: tenant.name, domain: tenant.domain })
  const [dp_form, setDPForm]  = useState({ full_name: dp.full_name, mobile: dp.mobile, new_password: '' })
  const [errors, setErrors]   = useState<Record<string, string>>({})
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [showPwd, setShowPwd] = useState(false)

  const setC = (k: string, v: string) => setCompany(f => {
    const next = { ...f, [k]: k === 'domain' ? v.toLowerCase().replace(/[^a-z0-9.-]/g, '') : v }
    setErrors(prev => {
      if (k === 'domain' && /^[a-z0-9.-]*$/.test(next.domain)) { const { domain: _, ...rest } = prev; return rest }
      return prev
    })
    return next
  })
  const setD = (k: string, v: string) => setDPForm(f => {
    const next = { ...f, [k]: k === 'mobile' ? v.replace(/[^+\d]/g, '') : v }
    setErrors(prev => {
      if (k === 'mobile') {
        const digits = next.mobile.replace(/\D/g, '')
        if (!digits || (digits.length >= 10 && digits.length <= 15)) { const { mobile: _, ...rest } = prev; return rest }
      }
      return prev
    })
    return next
  })

  const validate = (): boolean => {
    const e: Record<string, string> = {}
    const mobileDigits = dp_form.mobile.replace(/\D/g, '')
    if (dp_form.mobile && (mobileDigits.length < 10 || mobileDigits.length > 15)) e.mobile = 'Mobile must be 10-15 digits'
    if (company.domain && /[^a-z0-9.-]/.test(company.domain)) e.domain = 'Only lowercase letters, numbers, hyphens, and dots allowed'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSaving(true); setError('')
    try {
      // ── Company fields ────────────────────────────────────────────────────
      const tenantPayload: Record<string, string> = {}
      if (company.name.trim()   !== tenant.name)   tenantPayload.name   = company.name.trim()
      if (company.domain.trim() !== tenant.domain) tenantPayload.domain = company.domain.trim()

      // ── Dealer Principal fields ───────────────────────────────────────────
      const dpPayload: Record<string, string> = {}
      if (dp_form.full_name.trim() !== dp.full_name) dpPayload.full_name  = dp_form.full_name.trim()
      if (dp_form.mobile.trim()    !== dp.mobile)    dpPayload.mobile     = dp_form.mobile.trim()
      if (dp_form.new_password.trim())               dpPayload.new_password = dp_form.new_password.trim()

      if (Object.keys(tenantPayload).length === 0 && Object.keys(dpPayload).length === 0) {
        onClose(); return
      }

      // Run both updates in parallel; if either fails the error is shown
      const [updatedTenant, updatedDP] = await Promise.all([
        Object.keys(tenantPayload).length > 0
          ? adminService.updateTenant(tenant.id, tenantPayload)
          : Promise.resolve(tenant),
        Object.keys(dpPayload).length > 0
          ? adminService.updateUser(dp.id, dpPayload)
          : Promise.resolve(dp),
      ])

      onSaved(updatedTenant, updatedDP)
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Edit Dealer</h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{tenant.name}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">

          {/* ── Company ──────────────────────────────────────────────────── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-3"
              style={{ color: 'var(--text-tertiary)' }}>Company</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group col-span-2">
                <label className="form-label">Company Name <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input
                  className="input"
                  required
                  value={company.name}
                  onChange={e => setC('name', e.target.value)}
                  placeholder="Apex Motors Pvt. Ltd."
                />
              </div>
              <div className="form-group col-span-2">
                <label className="form-label">Domain <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                <input
                  className="input"
                  required
                  value={company.domain}
                  onChange={e => setC('domain', e.target.value)}
                  placeholder="apexmotors"
                />
                {errors.domain && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.domain}</p>}
                {!errors.domain && <p className="form-hint">Unique slug — lowercase letters, numbers, hyphens, and dots (e.g. khanna-motors.local)</p>}
              </div>
            </div>
          </div>

          {/* ── Dealer Principal ─────────────────────────────────────────── */}
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide mb-3"
              style={{ color: 'var(--text-tertiary)' }}>Dealer Principal</p>

            <div className="space-y-3">
              <div className="form-group">
                <label className="form-label">
                  Email <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}>(login — read only)</span>
                </label>
                <input
                  className="input"
                  value={dp.email}
                  readOnly
                  style={{ background: 'var(--surface-subtle)', color: 'var(--text-tertiary)', cursor: 'default' }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="form-group col-span-2">
                  <label className="form-label">Full Name <span aria-hidden="true" style={{ color: 'var(--danger-text)' }}>*</span></label>
                  <input
                    className="input"
                    required
                    value={dp_form.full_name}
                    onChange={e => setD('full_name', e.target.value)}
                  />
                </div>
                <div className="form-group col-span-2">
                  <label className="form-label">Mobile</label>
                  <input
                    className="input"
                    value={dp_form.mobile}
                    onChange={e => setD('mobile', e.target.value)}
                    placeholder="+919876543210"
                  />
                  {errors.mobile && <p className="form-hint" style={{ color: 'var(--danger-text)' }}>{errors.mobile}</p>}
                </div>
              </div>

              <div>
                <button
                  type="button"
                  className="text-sm font-medium flex items-center gap-1"
                  style={{ color: 'var(--brand-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => setShowPwd(v => !v)}
                >
                  {showPwd ? '▾' : '▸'} Reset password
                </button>
                {showPwd && (
                  <div className="form-group mt-2">
                    <PasswordInput
                      placeholder="New password (min. 12 chars)"
                      minLength={12}
                      value={dp_form.new_password}
                      onChange={e => setD('new_password', e.target.value)}
                      autoComplete="new-password"
                    />
                    <p className="form-hint">Leave blank to keep existing password.</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && <div className="alert-danger text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Tenant Row ────────────────────────────────────────────────────────────────
// ── Assign Plan Modal (Admin Panel) ──────────────────────────────────────────
// Handles both first-time plan assignment AND plan changes for tenants that
// already have an active subscription (subscribe() would 409 — so we detect
// the existing subscription on mount and call assignTier() directly instead).

const CYCLES = [{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }]

function AdminAssignPlanModal({
  tenant, pricingCatalog, planTiers, onClose, onDone,
}: {
  tenant: Tenant; pricingCatalog: PricingCatalog | null; planTiers: string[]
  onClose: () => void; onDone: () => void
}) {
  const [services, setServices]             = useState<BillingService[]>([])
  const [existingSubId, setExistingSubId]   = useState<number | null>(null)
  const [loadingSvc, setLoadingSvc]         = useState(true)
  const [form, setForm]                     = useState({ pricing_tier: planTiers[0] ?? '', billing_cycle: 'monthly' })
  const [saving, setSaving]                 = useState(false)
  const [error, setError]                   = useState('')

  useEffect(() => {
    // Fetch service catalog AND the tenant's existing subscriptions in parallel.
    // If the tenant already has an active CORE subscription we skip subscribe()
    // and call assignTier() directly to avoid a 409 "already subscribed" error.
    Promise.all([
      billingService.listServices(),
      billingService.getTenantSubscriptions(tenant.id),
    ]).then(([svcs, subs]) => {
      setServices(svcs)
      const coreService = svcs.find(s => s.service_type === 'CORE' && s.is_active)
      if (coreService) {
        const activeSub = subs.find(
          s => s.service_id === coreService.id && s.status?.toLowerCase() === 'active'
        )
        if (activeSub) {
          setExistingSubId(activeSub.id)
          // Pre-fill form with the current plan so the admin sees what's already assigned
          setForm({
            pricing_tier: activeSub.pricing_tier ?? planTiers[0] ?? '',
            billing_cycle: activeSub.billing_cycle?.toLowerCase() ?? 'monthly',
          })
        }
      }
    }).catch(() => setError('Could not load service catalog — check backend.'))
      .finally(() => setLoadingSvc(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const coreService  = services.find(s => s.service_type === 'CORE' && s.is_active)
  const coreSlug     = coreService?.slug ?? ''
  const estimatedCost = coreSlug
    ? pricingCatalog?.services?.[coreSlug]?.tiers?.[form.pricing_tier]?.[form.billing_cycle as 'monthly' | 'annual']
    : null
  const isChanging = existingSubId !== null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!coreService) { setError('CORE service not found — check service catalog in Billing tab.'); return }
    setSaving(true); setError('')
    try {
      let subId = existingSubId
      if (subId === null) {
        // No active subscription yet — create one
        const sub = await billingService.subscribe({ tenant_id: tenant.id, service_id: coreService.id })
        subId = sub.id
      }
      await billingService.assignTier(subId, { pricing_tier: form.pricing_tier, billing_cycle: form.billing_cycle })
      // Sync subscription_tier on the Tenant record so the Plan badge reflects
      // the new plan immediately after load() refreshes the tenant list.
      await adminService.updateTenant(tenant.id, { subscription_tier: form.pricing_tier })
      onDone(); onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Failed to assign plan.')
    } finally { setSaving(false) }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 380 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {isChanging ? 'Change Plan' : 'Assign Plan'}
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {tenant.name} · {tenant.domain}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        {loadingSvc ? (
          <div className="flex justify-center py-8"><div className="spinner" /></div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="input" value={form.pricing_tier}
                  onChange={e => setForm(f => ({ ...f, pricing_tier: e.target.value }))}>
                  {planTiers.map(t => (
                    <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Billing Cycle</label>
                <select className="input" value={form.billing_cycle}
                  onChange={e => setForm(f => ({ ...f, billing_cycle: e.target.value }))}>
                  {CYCLES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
            </div>

            {estimatedCost != null && (
              <div className="rounded-lg px-3 py-2.5 text-sm"
                style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
                Cost: <strong>
                  {fmt(Number(estimatedCost))}
                </strong>/{form.billing_cycle === 'annual' ? 'yr' : 'mo'}
                {' '}— debited from tenant's wallet per billing cycle.
              </div>
            )}

            {error && <div className="alert-danger text-sm">{error}</div>}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn-primary"
                disabled={saving || !coreService || planTiers.length === 0}>
                {saving ? 'Assigning…' : 'Assign Plan'}
              </button>
            </div>
          </form>
        )}
      </div>
    </ModalOverlay>
  )
}

// ── Delete Dealer Confirmation Modal ─────────────────────────────────────────

function DeleteDealerModal({
  tenant, onClose, onDeleted,
}: {
  tenant: Tenant; onClose: () => void; onDeleted: () => void
}) {
  const [confirm, setConfirm] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState('')
  const match = confirm.trim().toLowerCase() === tenant.name.trim().toLowerCase()

  const handleDelete = async () => {
    if (!match) return
    setDeleting(true); setError('')
    try {
      await adminService.deleteTenant(tenant.id)
      onDeleted()
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(msg || 'Delete failed — check backend logs.')
    } finally { setDeleting(false) }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--danger-text)' }}>
              Delete Dealer
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              This action is permanent and cannot be undone.
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg p-3" style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border, var(--danger-text))' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--danger-text)' }}>
              Permanently deletes <strong>{tenant.name}</strong> and ALL associated data:
              leads, customers, users, subscriptions, wallet, call logs, audit trail.
            </p>
          </div>

          <div className="form-group">
            <label className="form-label">
              Type <strong>{tenant.name}</strong> to confirm
            </label>
            <input
              className="input"
              placeholder={tenant.name}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="off"
            />
          </div>

          {error && <div className="alert-danger text-sm">{error}</div>}

          <div className="flex justify-end gap-2">
            <button className="btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn-primary"
              style={match ? { background: 'var(--danger-text)', borderColor: 'var(--danger-text)' } : {}}
              disabled={!match || deleting}
              onClick={handleDelete}
            >
              {deleting ? 'Deleting…' : 'Delete Permanently'}
            </button>
          </div>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Mobile card list ─────────────────────────────────────────────────────────

function MobileDealerCards({ tenants, walletByTenant, usersByTenant, togglingTenant,
  onAssignPlan, onDelete, onEditDP, onChannels, onBranding, onFeatures, onToggle,
}: {
  tenants: Tenant[]
  walletByTenant: Record<number, WalletType>
  usersByTenant: Record<number, AppUser[]>
  togglingTenant: number | null
  onAssignPlan: (t: Tenant) => void
  onDelete: (t: Tenant) => void
  onEditDP: (t: Tenant) => void
  onChannels: (t: Tenant) => void
  onBranding: (t: Tenant) => void
  onFeatures: (t: Tenant) => void
  onToggle: (t: Tenant) => void
}) {
  const [expandedId, setExpandedId] = useState<number | null>(null)

  return (
    <div className="space-y-2">
      {tenants.map(t => {
        const wallet     = walletByTenant[t.id]
        const balance    = wallet ? Number(wallet.balance) : null
        const isLow      = balance != null && balance < 500
        const teamUsers  = usersByTenant[t.id] ?? []
        const dp         = teamUsers.find(u => u.role === UserRole.DEALER_PRINCIPAL && u.is_active)
        const activeTeam = teamUsers.filter(u => u.is_active)
        const healthColor = !t.is_active ? 'var(--border-strong)' : isLow ? 'var(--warning-text)' : 'var(--brand-primary)'
        const expanded   = expandedId === t.id

        return (
          <div key={t.id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div
              className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
              style={{ borderLeft: `3px solid ${healthColor}` }}
              onClick={() => setExpandedId(expanded ? null : t.id)}
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{t.name}</p>
                <p className="font-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{t.domain}</p>
              </div>
              {balance != null && (
                <span className="text-sm font-semibold tabular-nums shrink-0"
                  style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}>
                  {fmt(balance)}
                  {isLow && <span className="ml-1 badge-danger" style={{ fontSize: 9, padding: '1px 4px', verticalAlign: 'middle' }}>LOW</span>}
                </span>
              )}
              <span className={t.is_active ? 'badge-success' : 'badge-danger'} style={{ fontSize: 10 }}>
                {t.is_active ? 'Active' : 'Inactive'}
              </span>
              <ChevronDown
                className="h-4 w-4 shrink-0 transition-transform"
                style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
              />
            </div>

            {expanded && (
              <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Dealer Principal</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{dp?.full_name ?? '—'}</span>
                </div>
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Team</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                    {activeTeam.length > 0 ? activeTeam.length : '—'}
                  </span>
                </div>
                <div className="flex justify-between text-xs py-1">
                  <span style={{ color: 'var(--text-tertiary)' }}>Since</span>
                  <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{fmtDate(t.created_at)}</span>
                </div>
                <div className="flex items-center gap-1 mt-3 pt-2" style={{ borderTop: '1px solid var(--border-default)' }}>
                  <button onClick={e => { e.stopPropagation(); onEditDP(t) }} className="btn-icon" title="Edit Dealer Principal">
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); onAssignPlan(t) }} className="btn-icon" title="Assign / change plan">
                    <CreditCard className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); onChannels(t) }} className="btn-icon" title="Channels">
                    <Phone className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); onBranding(t) }} className="btn-icon" title="Branding">
                    <Palette className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={e => { e.stopPropagation(); onFeatures(t) }} className="btn-icon" title="Features">
                    <Sliders className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); onToggle(t) }}
                    disabled={togglingTenant === t.id}
                    className="btn-icon"
                    title={t.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {togglingTenant === t.id ? (
                      <div className="spinner" style={{ width: 13, height: 13 }} />
                    ) : t.is_active ? (
                      <ToggleRight className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
                    ) : (
                      <ToggleLeft className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                    )}
                  </button>
                  <button onClick={e => { e.stopPropagation(); onDelete(t) }} className="btn-icon" title="Delete dealer permanently">
                    <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ── Table row ─────────────────────────────────────────────────────────────────
// Same visual logic as the old cards: health-colored left border,
// wallet coloring, DP name, role breakdown, icon actions.

function TenantRow({ tenant, wallet, teamUsers, toggling,
  onChannels, onBranding, onFeatures, onToggle, onEditDP, onAssignPlan, onDelete }: {
  tenant: Tenant; wallet: WalletType | undefined
  teamUsers: AppUser[]
  toggling: boolean
  onChannels: () => void; onBranding: () => void
  onFeatures: () => void; onToggle: () => void; onEditDP: () => void
  onAssignPlan: () => void; onDelete: () => void
}) {
  const balance = wallet ? Number(wallet.balance) : null
  const isLow   = balance != null && balance < 500

  const healthColor = !tenant.is_active
    ? 'var(--border-strong)'
    : isLow
      ? 'var(--warning-text)'
      : 'var(--brand-primary)'

  const dp         = teamUsers.find(u => u.role === UserRole.DEALER_PRINCIPAL && u.is_active)
  const activeTeam = teamUsers.filter(u => u.is_active)

  const roleBreakdown = [
    { role: UserRole.INSURANCE_HEAD, abbr: 'IH' },
    { role: UserRole.TEAM_LEADER,    abbr: 'TL' },
    { role: UserRole.TELECALLER,     abbr: 'TC' },
  ]
    .map(({ role, abbr }) => {
      const n = activeTeam.filter(u => u.role === role).length
      return n > 0 ? `${n} ${abbr}` : null
    })
    .filter(Boolean).join(' · ')

  const BTN: React.CSSProperties = { width: 28, height: 28, flexShrink: 0 }

  return (
    <tr
      onMouseEnter={e => (e.currentTarget.style.background = 'var(--surface-hover, var(--surface-subtle))')}
      onMouseLeave={e => (e.currentTarget.style.background = '')}
      style={{ transition: 'background 0.1s' }}
    >
      {/* Tenant — carries the health border */}
      <td style={{ borderLeft: `3px solid ${healthColor}`, paddingLeft: 14 }}>
        <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{tenant.name}</p>
        <p className="font-mono" style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
          {tenant.domain}
        </p>
      </td>

      {/* Dealer Principal */}
      <td>
        {dp ? (
          <p className="text-sm font-medium truncate" title={dp.full_name} style={{ color: 'var(--text-primary)', maxWidth: 160 }}>
            {dp.full_name}
          </p>
        ) : (
          <p style={{ fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>—</p>
        )}
      </td>

      {/* Wallet */}
      <td>
        {balance != null ? (
          <span className="font-semibold tabular-nums text-sm"
            style={{ color: isLow ? 'var(--danger-text)' : 'var(--success-text)' }}>
            {fmt(balance)}
            {isLow && (
              <span className="ml-1.5 badge-danger" style={{ fontSize: 9, padding: '1px 4px', verticalAlign: 'middle' }}>
                LOW
              </span>
            )}
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>—</span>
        )}
      </td>

      {/* Team */}
      <td>
        {activeTeam.length > 0 ? (
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
            <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{activeTeam.length}</span>
            {roleBreakdown && <span> · {roleBreakdown}</span>}
          </p>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>—</span>
        )}
      </td>

      {/* Status */}
      <td>
        <span className={tenant.is_active ? 'badge-success' : 'badge-danger'} style={{ fontSize: 10 }}>
          {tenant.is_active ? 'Active' : 'Inactive'}
        </span>
      </td>

      {/* Since */}
      <td style={{ fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
        {fmtDate(tenant.created_at)}
      </td>

      {/* Actions */}
      <td>
        <div className="flex items-center gap-0.5">
          <button onClick={onEditDP}    className="btn-icon" title="Edit Dealer Principal" style={BTN}>
            <Pencil    className="h-3.5 w-3.5" />
          </button>
          <button onClick={onAssignPlan} className="btn-icon" title="Assign / change plan" style={BTN}>
            <CreditCard className="h-3.5 w-3.5" />
          </button>
          <button onClick={onChannels}  className="btn-icon" title="Channels"  style={BTN}>
            <Phone     className="h-3.5 w-3.5" />
          </button>
          <button onClick={onBranding}  className="btn-icon" title="Branding"  style={BTN}>
            <Palette   className="h-3.5 w-3.5" />
          </button>
          <button onClick={onFeatures}  className="btn-icon" title="Features"  style={BTN}>
            <Sliders   className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={onToggle} disabled={toggling}
            className="btn-icon"
            title={tenant.is_active ? 'Deactivate' : 'Activate'}
            style={BTN}
          >
            {toggling ? (
              <div className="spinner" style={{ width: 13, height: 13 }} />
            ) : tenant.is_active ? (
              <ToggleRight className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
            ) : (
              <ToggleLeft  className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
            )}
          </button>
          <button
            onClick={onDelete}
            className="btn-icon"
            title="Delete dealer permanently"
            style={BTN}
          >
            <Trash2 className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />
          </button>
        </div>
      </td>
    </tr>
  )
}

// ── Skeleton row (loading state) ──────────────────────────────────────────────

function SkeletonRow() {
  const pulse: React.CSSProperties = {
    display: 'inline-block', borderRadius: 4,
    background: 'var(--surface-subtle)',
    animation: 'pulse 1.5s ease-in-out infinite',
  }
  return (
    <tr>
      <td style={{ borderLeft: '3px solid var(--border-default)', paddingLeft: 14 }}>
        <div style={{ ...pulse, height: 13, width: 120 }} />
        <div style={{ ...pulse, height: 11, width: 80, marginTop: 4 }} />
      </td>
      <td><div style={{ ...pulse, height: 13, width: 100 }} /></td>
      <td><div style={{ ...pulse, height: 13, width: 72 }} /></td>
      <td><div style={{ ...pulse, height: 13, width: 90 }} /></td>
      <td><div style={{ ...pulse, height: 18, width: 52 }} /></td>
      <td><div style={{ ...pulse, height: 13, width: 72 }} /></td>
      <td><div style={{ ...pulse, height: 28, width: 140 }} /></td>
    </tr>
  )
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const [tenants, setTenants]         = useState<Tenant[]>([])
  const [users,   setUsers]           = useState<AppUser[]>([])
  const [wallets, setWallets]         = useState<WalletType[]>([])
  const [planTiers, setPlanTiers]     = useState<string[]>(['basic', 'standard'])
  const [pricingCatalog, setPricingCatalog] = useState<PricingCatalog | null>(null)
  const [loading, setLoading]         = useState(true)
  const [error,   setError]           = useState('')

  const [showOnboard, setShowOnboard]           = useState(false)
  const [assignPlanTenant, setAssignPlanTenant] = useState<Tenant | null>(null)
  const [deleteTenant, setDeleteTenant]         = useState<Tenant | null>(null)
  const [channelTenant, setChannelTenant]   = useState<Tenant | null>(null)
  const [themeTenant, setThemeTenant]       = useState<Tenant | null>(null)
  const [featureTenant, setFeatureTenant]   = useState<Tenant | null>(null)
  const [editDPFor, setEditDPFor]           = useState<{ tenant: Tenant; dp: AppUser } | null>(null)
  const [togglingTenant, setTogglingTenant] = useState<number | null>(null)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const isMobile = useWindowWidth() < 768

  const showToast = (type: 'success' | 'error', text: string) => {
    setToast({ type, text })
    setTimeout(() => setToast(null), 4000)
  }

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [t, u, w, pricing] = await Promise.all([
        adminService.listTenants(),
        adminService.listUsers(),
        adminService.listWallets(),
        billingService.getPricing().catch(() => null),  // non-fatal — falls back to default
      ])
      setTenants(t); setUsers(u); setWallets(w)
      // Keep open branding modal in sync with freshly loaded tenant data
      setThemeTenant(prev => prev ? (t.find((x: Tenant) => x.id === prev.id) ?? prev) : null)
      // Extract non-addon tiers from the first core service in the catalog.
      // Resolved dynamically from pricing.services so a slug rename in
      // pricing.json is picked up without touching this file.
      if (pricing) {
        setPricingCatalog(pricing)
        const coreTiers = Object.values(pricing.services ?? {})
          .flatMap(svc => Object.keys(svc.tiers ?? {}))
          .filter(tier => tier !== 'addon')
        const uniqueTiers = [...new Set(coreTiers)]
        if (uniqueTiers.length > 0) setPlanTiers(uniqueTiers)
      }
    } catch {
      setError('Failed to load admin data. Check network or permissions.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const toggleTenant = async (t: Tenant) => {
    setTogglingTenant(t.id)
    try {
      const updated = await adminService.updateTenant(t.id, { is_active: !t.is_active })
      setTenants(prev => prev.map(x => x.id === t.id ? updated : x))
    } finally {
      setTogglingTenant(null)
    }
  }

  // Exclude BACKEND_ADMIN from all tenant-facing computations.
  const dealerUsers   = users.filter(u => u.role !== UserRole.BACKEND_ADMIN)
  const dealerTenants = tenants
  const usersByTenant  = dealerUsers.reduce<Record<number, AppUser[]>>((acc, u) => {
    ;(acc[u.tenant_id] ||= []).push(u); return acc
  }, {})
  const walletByTenant = Object.fromEntries(wallets.map(w => [w.tenant_id, w]))

  return (
    <div className="space-y-5">

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Shield className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
            Admin Panel
          </h1>
          <StatsStrip tenants={dealerTenants} users={dealerUsers} wallets={wallets} loading={loading} />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={loading} className="btn-icon" title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setShowOnboard(true)} className="btn-primary">
            <Plus className="h-3.5 w-3.5" />
            New Dealer
          </button>
        </div>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {/* ── Dealers table / mobile cards ──────────────────────────────────── */}
      {isMobile ? (
        loading ? (
          <div className="flex justify-center py-10"><div className="spinner" /></div>
        ) : dealerTenants.length === 0 ? (
          <div className="empty-state">
            <Building2 className="h-8 w-8 mb-3" style={{ opacity: 0.3, color: 'var(--text-tertiary)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No dealers yet</p>
            <button onClick={() => setShowOnboard(true)} className="btn-primary" style={{ height: 32, fontSize: 13 }}>
              <Plus className="h-3.5 w-3.5" /> Onboard first dealer
            </button>
          </div>
        ) : (
          <MobileDealerCards
            tenants={dealerTenants}
            walletByTenant={walletByTenant}
            usersByTenant={usersByTenant}
            togglingTenant={togglingTenant}
            onAssignPlan={t => setAssignPlanTenant(t)}
            onDelete={t => setDeleteTenant(t)}
            onEditDP={t => {
              const dp = (usersByTenant[t.id] ?? []).find(
                u => u.role === UserRole.DEALER_PRINCIPAL && u.is_active
              )
              if (dp) setEditDPFor({ tenant: t, dp })
            }}
            onChannels={t => setChannelTenant(t)}
            onBranding={t => setThemeTenant(t)}
            onFeatures={t => setFeatureTenant(t)}
            onToggle={t => toggleTenant(t)}
          />
        )
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dealer</th>
                  <th>Dealer Principal</th>
                  <th>Wallet</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Since</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 3 }).map((_, i) => <SkeletonRow key={i} />)
                ) : dealerTenants.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center">
                      <div className="flex flex-col items-center gap-3" style={{ color: 'var(--text-tertiary)' }}>
                        <Building2 className="h-8 w-8" style={{ opacity: 0.3 }} />
                        <p className="text-sm font-medium">No dealers yet</p>
                        <button onClick={() => setShowOnboard(true)} className="btn-primary"
                          style={{ height: 32, fontSize: 13 }}>
                          <Plus className="h-3.5 w-3.5" /> Onboard first dealer
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : (
                  dealerTenants.map(t => (
                    <TenantRow
                      key={t.id}
                      tenant={t}
                      wallet={walletByTenant[t.id]}
                      teamUsers={usersByTenant[t.id] ?? []}
                      toggling={togglingTenant === t.id}
                      onAssignPlan={() => setAssignPlanTenant(t)}
                      onDelete={() => setDeleteTenant(t)}
                      onEditDP={() => {
                        const dp = (usersByTenant[t.id] ?? []).find(
                          u => u.role === UserRole.DEALER_PRINCIPAL && u.is_active
                        )
                        if (dp) setEditDPFor({ tenant: t, dp })
                      }}
                      onChannels={() => setChannelTenant(t)}
                      onBranding={() => setThemeTenant(t)}
                      onFeatures={() => setFeatureTenant(t)}
                      onToggle={() => toggleTenant(t)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}
      {showOnboard && <OnboardDealerModal onClose={() => setShowOnboard(false)} onDone={load} />}
      {assignPlanTenant && (
        <AdminAssignPlanModal
          tenant={assignPlanTenant}
          pricingCatalog={pricingCatalog}
          planTiers={planTiers}
          onClose={() => setAssignPlanTenant(null)}
          onDone={load}
        />
      )}
      {editDPFor && (
        <EditDealerModal
          tenant={editDPFor.tenant}
          dp={editDPFor.dp}
          onClose={() => setEditDPFor(null)}
          onSaved={(updatedTenant, updatedDP) => {
            setTenants(prev => prev.map(t => t.id === updatedTenant.id ? updatedTenant : t))
            setUsers(prev => prev.map(u => u.id === updatedDP.id ? updatedDP : u))
            setEditDPFor(null)
          }}
        />
      )}
      {deleteTenant && (
        <DeleteDealerModal
          tenant={deleteTenant}
          onClose={() => setDeleteTenant(null)}
          onDeleted={() => {
            setTenants(prev => prev.filter(t => t.id !== deleteTenant.id))
            setUsers(prev => prev.filter(u => u.tenant_id !== deleteTenant.id))
            setWallets(prev => prev.filter(w => w.tenant_id !== deleteTenant.id))
          }}
        />
      )}
      {channelTenant && <ChannelConfigModal tenant={channelTenant} onClose={() => setChannelTenant(null)} />}
      {themeTenant   && <BrandingModal tenant={themeTenant} onClose={() => setThemeTenant(null)} onSaved={load} />}
      {featureTenant && (
        <FeatureFlagsModal
          tenant={featureTenant}
          onClose={() => setFeatureTenant(null)}
          onSaved={updated => {
            setTenants(prev => prev.map(t => t.id === updated.id ? updated : t))
            setFeatureTenant(null)
            showToast('success', `Feature flags updated for ${updated.name}.`)
          }}
        />
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-5 right-5 z-50 flex items-start gap-3 px-4 py-3 rounded-lg"
          style={{
            maxWidth: 'min(24rem, calc(100vw - 2.5rem))',
            // Solid accent fill so it can never blend into the neutral table/cards.
            // Darker green/red (700) keeps white text at ~AA contrast.
            background: toast.type === 'success' ? '#15803d' : '#b91c1c',
            boxShadow: 'var(--shadow-xl)',
          }}
        >
          {toast.type === 'success'
            ? <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#ffffff' }} />
            : <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#ffffff' }} />}
          <span className="text-sm font-bold" style={{ color: '#ffffff' }}>{toast.text}</span>
        </div>
      )}
    </div>
  )
}
